import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import { Button } from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { ArrowRight, Zap, Code, Database, Globe } from "lucide-react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    right: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    left: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const IntegrationsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 64px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const IntegrationCard = styled.div`
  padding: 32px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  transition: all 0.5s ease-out;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const IntegrationIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: white;
`;

const IntegrationName = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const IntegrationDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const CTASection = styled.section`
  padding: 96px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    flex-direction: row;
  }
`;

const integrations = [
  { name: "Slack", description: "Get real-time notifications and updates", icon: Zap },
  { name: "GitHub", description: "Sync your repositories and track deployments", icon: Code },
  { name: "Stripe", description: "Process payments and manage subscriptions", icon: Database },
  { name: "Google Workspace", description: "Connect your calendar and email", icon: Globe },
  { name: "Zapier", description: "Automate workflows with 5000+ apps", icon: Zap },
  { name: "Webhooks", description: "Custom integrations via HTTP endpoints", icon: Code },
];

export default function Integrations() {
  return (
    <Layout>
      <SEO
        title="Integrations"
        description="Connect Kliq CRM with your favorite tools and services. Integrate with Slack, GitHub, Stripe, and more."
        keywords="integrations, API, webhooks, Slack, GitHub, Stripe"
        hreflang
      />
      <PageTransition>
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">Integrations</Badge>
              </BadgeWrapper>
              <Title>
                Connect with your <GradientText>favorite tools</GradientText>
              </Title>
              <Description>
                Seamlessly integrate Kliq CRM with the services you already use. Connect, automate, and streamline your workflow.
              </Description>
            </HeroContent>

            <IntegrationsGrid>
              {integrations.map((integration) => (
                <IntegrationCard key={integration.name}>
                  <IntegrationIcon>
                    <integration.icon size={24} />
                  </IntegrationIcon>
                  <IntegrationName>{integration.name}</IntegrationName>
                  <IntegrationDescription>{integration.description}</IntegrationDescription>
                </IntegrationCard>
              ))}
            </IntegrationsGrid>
          </Container>
        </Section>

        <CTASection>
          <Container>
            <HeroContent>
              <Title style={{ fontSize: "clamp(1.875rem, 3vw, 2.25rem)" }}>Need a custom integration?</Title>
              <Description style={{ marginBottom: "32px" }}>
                We're always adding new integrations. Request one or build your own with our API.
              </Description>
              <CTAWrapper>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button variant="hero" size="lg">
                    Request Integration
                    <ArrowRight size={20} />
                  </Button>
                </Link>
                <Link to="/api-reference" style={{ textDecoration: "none" }}>
                  <Button variant="outline" size="lg">
                    View API Docs
                  </Button>
                </Link>
              </CTAWrapper>
            </HeroContent>
          </Container>
        </CTASection>
      </PageTransition>
    </Layout>
  );
}
